import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, CheckCircle2, Loader2, Download, WifiOff, ShieldCheck, Smartphone, X, KeyRound, Share2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import PatientHealthFileFounderReplica from '@/pages/PatientHealthFileFounderReplica';
import {
  saveDhfLocal, getDhfLocal, requestPersistentStorage,
  saveDhfLocalSecure, getDhfLocalSecure, hasSecureLocal,
} from '@/lib/dhfOffline';
import { downloadDhfPdf } from '@/lib/dhfPdf';
import DhfAccessGate from '@/components/healthfile/DhfAccessGate';
import DhfShareModal from '@/components/healthfile/DhfShareModal';

const NAVY = '#16335b';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
}

/* Map the backend public-viewer payload into the approved replica's data shape. */
function mapDhf(dhf) {
  const created = `${fmtDate(dhf.created_at)}${dhf.created_at_time_str ? '  •  ' + dhf.created_at_time_str : ''}`;
  return {
    patient: { name: dhf.patient?.name, age: dhf.patient?.age, gender: dhf.patient?.gender },
    visit_date: fmtDate(dhf.visit_date) || fmtDate(dhf.created_at),
    doctor: {
      name: dhf.doctor?.name,
      specialty: dhf.doctor?.specialty,
      qualifications: dhf.doctor?.qualifications,
      registration: dhf.doctor?.registration,
      signature_url: dhf.doctor?.signature_url,
      slug: dhf.doctor?.slug,
    },
    consultation_type: dhf.consultation_type,
    clinic_location: dhf.clinic_location || dhf.location,
    reason_for_visit: dhf.reason_for_visit,
    assessment: (dhf.doctor_assessment || '').split('\n').map(s => s.trim()).filter(Boolean),
    medicines: dhf.medicines || [],
    rx_attachments: dhf.rx_attachments || [],
    thread_progress: dhf.thread_progress || [],
    tests: dhf.tests_advised || [],
    red_flags: dhf.red_flags || [],
    follow_up: dhf.follow_up_plan,
    // v9 · visit-level Do's / Don'ts (Simple condition flow). Legacy DHFs return [].
    dos: dhf.dos || [],
    donts: dhf.donts || [],
    file_id: dhf.file_id,
    created_at_str: created,
    signature_url: dhf.doctor?.signature_url,
  };
}

export default function PatientHealthFilePage() {
  const { token } = useParams();
  const [dhf, setDhf] = useState(null);          // GET metadata payload
  const [unlocked, setUnlocked] = useState(null); // full payload (with clinical) after gate
  const [pin, setPin] = useState('');             // master PIN held in-memory only
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [source, setSource] = useState('online');
  const [optInGate, setOptInGate] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // offline-secure PIN unlock
  const [offlinePinNeeded, setOfflinePinNeeded] = useState(false);
  const [offlinePin, setOfflinePin] = useState('');
  const [offlineErr, setOfflineErr] = useState('');
  // save-to-phone
  const [showConsent, setShowConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [ring, setRing] = useState(0);
  const [savedMsg, setSavedMsg] = useState('');
  const [offlineReady, setOfflineReady] = useState(false);
  // Patient-mediated DHF share modal (QR + WhatsApp forward, zero-SMS).
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/health-file/${token}`);
        if (!live) return;
        setDhf(data);
        setSource('online');
        // Persist a durable on-device copy ONLY for legacy, non-paywalled files
        // (these have clinical data here). Secured files are cached encrypted
        // after the gate unlock instead.
        const hasClinical = data && !data.paywalled && data.reason_for_visit !== undefined;
        if (hasClinical && !data.security?.secure_gate) {
          saveDhfLocal(token, data).then((ok) => { if (live && ok) setOfflineReady(true); });
        }
      } catch (e) {
        // Network failed → fall back to a durable on-device copy if we have one.
        const local = await getDhfLocal(token);
        if (!live) return;
        if (local) {
          setUnlocked(local); setSource('offline'); setOfflineReady(true); setErr('');
        } else if (await hasSecureLocal(token)) {
          setOfflinePinNeeded(true); setSource('offline');
        } else {
          setErr(formatApiError(e.response?.data?.detail) || 'Health file not found');
        }
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [token]);

  const onUnlocked = (payload, usedPin) => {
    setUnlocked(payload);
    setPin(usedPin || '');
    setOptInGate(false);
    // Encrypt + cache offline using the PIN-derived key.
    if (usedPin) saveDhfLocalSecure(token, payload, usedPin).then((ok) => ok && setOfflineReady(true));
  };

  const tryOfflineUnlock = async () => {
    setOfflineErr('');
    const data = await getDhfLocalSecure(token, offlinePin);
    if (data) { setUnlocked(data); setPin(offlinePin); setOfflinePinNeeded(false); setOfflineReady(true); }
    else setOfflineErr('Incorrect PIN for the offline copy.');
  };

  const confirmSave = async () => {
    setSaving(true); setSavedMsg('');
    const data = unlocked || dhf;
    try {
      await requestPersistentStorage();
      if (pin) await saveDhfLocalSecure(token, data, pin);
      else await saveDhfLocal(token, data);
      setOfflineReady(true);
      downloadDhfPdf(mapDhf(data), data.file_id);
      setSaving(false); setSaved(true);
      const start = Date.now(); const DUR = 3000;
      const tick = () => {
        const p = Math.min(1, (Date.now() - start) / DUR);
        setRing(p);
        if (p < 1) requestAnimationFrame(tick);
        else { setShowConsent(false); setSaved(false); setRing(0); setSavedMsg('Saved to your phone ✓'); setTimeout(() => setSavedMsg(''), 4000); }
      };
      requestAnimationFrame(tick);
    } catch {
      setSaving(false); setSavedMsg('Could not save — please try again');
      setShowConsent(false); setTimeout(() => setSavedMsg(''), 5000);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#e7ddc9' }}>
      <Loader2 className="animate-spin" size={28} style={{ color: NAVY }} />
    </div>
  );

  // Offline secured copy → ask for PIN to decrypt locally.
  if (offlinePinNeeded && !unlocked) return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#e7ddc9', fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#e0d6c0] p-7 shadow-xl">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-50 border border-sky-100 mb-3"><WifiOff size={22} className="text-sky-600" /></div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">Offline copy</p>
        <h1 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Enter your PIN to view</h1>
        <p className="text-sm text-slate-600 mt-2">You're offline. Enter your 6-digit Master PIN to decrypt the copy saved on this phone.</p>
        <input type="password" inputMode="numeric" maxLength={6} value={offlinePin}
          onChange={(e) => setOfflinePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="w-full mt-3 text-center tracking-[0.6em] text-2xl font-bold py-3 rounded-2xl border border-slate-300 outline-none"
          data-testid="dhf-offline-pin" />
        <button onClick={tryOfflineUnlock} className="w-full mt-4 py-3.5 rounded-full text-white text-sm font-bold flex items-center justify-center gap-2" style={{ background: NAVY }} data-testid="dhf-offline-unlock"><KeyRound size={16} /> Unlock offline</button>
        {offlineErr && <p className="text-[13px] text-rose-700 mt-3 text-center">{offlineErr}</p>}
      </div>
    </div>
  );

  if (err && !unlocked && !dhf) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#e7ddc9' }}>
      <div className="bg-white rounded-2xl border border-[#e0d6c0] p-8 max-w-md text-center shadow-lg">
        <ShieldCheck size={28} className="text-slate-400 mx-auto mb-3" />
        <p className="text-base font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{err || 'File not found'}</p>
      </div>
    </div>
  );

  // ── Zero-trust gate: secured (or paywalled) file, not yet unlocked ──
  if (!unlocked && dhf && (dhf.security?.secure_gate || dhf.paywalled)) {
    return <DhfAccessGate token={token} dhf={dhf} onUnlocked={onUnlocked} />;
  }

  // ── Opt-in lock flow for legacy files (triggered by banner) ──
  if (optInGate && dhf) {
    return <DhfAccessGate token={token} dhf={dhf} optIn onUnlocked={onUnlocked} />;
  }

  // ── Unlocked / legacy-accessible file → founder-replica viewer ──
  const data = unlocked || dhf;
  if (!data || data.reason_for_visit === undefined) return null;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const isLegacyOptInEligible = dhf?.security?.can_opt_in && !bannerDismissed && source === 'online';

  return (
    <>
      <Helmet>
        <title>{`Digital Health File · ${data.patient?.name || 'Patient'} · DocSite`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <PatientHealthFileFounderReplica
        data={mapDhf(data)}
        shareUrl={shareUrl}
        doctorSlug={data.doctor?.slug}
        healthFileToken={token}
      />

      {/* Legacy opt-in: "lock this file to your device" banner */}
      {isLegacyOptInEligible && (
        <div className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-3 py-2 px-4 text-[12px] font-semibold text-white" style={{ background: NAVY }} data-testid="dhf-optin-banner">
          <ShieldCheck size={14} />
          <span>Lock this file to your device for extra security</span>
          <button onClick={() => setOptInGate(true)} className="px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25" data-testid="dhf-optin-lock">Lock now</button>
          <button onClick={() => setBannerDismissed(true)} className="text-white/70 hover:text-white" data-testid="dhf-optin-dismiss"><X size={14} /></button>
        </div>
      )}

      {/* Offline-source banner */}
      {source === 'offline' && (
        <div className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 py-1.5 text-[12px] font-semibold text-white" style={{ background: NAVY }} data-testid="dhf-offline-banner">
          <WifiOff size={13} /> Offline copy — saved on this phone
        </div>
      )}

      {/* Save-to-phone floating action */}
      <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-2" data-testid="dhf-save-bar">
        {savedMsg && (
          <div className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[12px] font-semibold shadow-lg" data-testid="dhf-saved-msg">{savedMsg}</div>
        )}
        {offlineReady && !savedMsg && (
          <div className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[11px] font-semibold shadow flex items-center gap-1.5" data-testid="dhf-offline-ready">
            <CheckCircle2 size={13} /> Available offline
          </div>
        )}
        <button onClick={() => setShowConsent(true)} disabled={saving}
          className="flex items-center gap-2 px-4 py-3 rounded-full text-white text-sm font-bold shadow-xl hover:opacity-90 disabled:opacity-60"
          style={{ background: NAVY }} data-testid="dhf-save-to-phone">
          {saving ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />}
          {saving ? 'Saving…' : 'Save to phone'}
        </button>
        {/* Share-with-a-doctor FAB — sits alongside "Save to phone" so
            patients can hand the DHF to another doctor over a QR + one
            WhatsApp tap. No SMS money burned by DocSite. */}
        <button onClick={() => setShowShare(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-emerald-300 text-emerald-700 text-xs font-bold shadow-lg hover:bg-emerald-50"
          data-testid="dhf-share-with-doctor">
          <Share2 size={14} /> Share with a doctor
        </button>
      </div>

      {showShare && (
        <DhfShareModal
          viewToken={token}
          patientName={data.patient?.name}
          doctorName={data.doctor?.name}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Consent dialog */}
      {showConsent && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => !saved && setShowConsent(false)} data-testid="dhf-consent-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {!saved ? (
              <>
                <div className="flex items-start justify-between">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-sky-50 border border-sky-100">
                    <Smartphone size={20} style={{ color: NAVY }} />
                  </div>
                  <button onClick={() => setShowConsent(false)} className="text-slate-400 hover:text-slate-600" data-testid="dhf-consent-close"><X size={18} /></button>
                </div>
                <h2 className="text-base font-bold text-slate-900 mt-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Save this file to your phone?</h2>
                <ul className="mt-3 space-y-2 text-[13px] text-slate-600">
                  <li className="flex items-start gap-2"><Download size={14} className="text-sky-600 mt-0.5 shrink-0" /> A PDF copy downloads to your phone’s Downloads folder — opens with no internet.</li>
                  <li className="flex items-start gap-2"><Lock size={14} className="text-sky-600 mt-0.5 shrink-0" /> {pin ? 'An encrypted copy (unlocked by your PIN) is kept inside DocSite on this device.' : 'A secure copy is kept inside DocSite on this device for instant offline viewing.'}</li>
                  <li className="flex items-start gap-2"><ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Your original stays securely backed up on DocSite.</li>
                </ul>
                <button onClick={confirmSave} disabled={saving} className="w-full mt-4 py-3 rounded-full text-white text-sm font-bold hover:opacity-90 disabled:opacity-70 flex items-center justify-center gap-2" style={{ background: NAVY }} data-testid="dhf-consent-confirm">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {saving ? 'Saving…' : 'Save to my phone'}
                </button>
                <p className="text-[10px] text-slate-400 mt-2 text-center">You control this copy. Delete it anytime from your phone.</p>
              </>
            ) : (
              <div className="flex flex-col items-center text-center py-3" data-testid="dhf-saved-state">
                <div className="relative w-16 h-16 mb-3">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#16a34a" strokeWidth="4" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 20} strokeDashoffset={(2 * Math.PI * 20) * ring} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><CheckCircle2 size={26} className="text-emerald-600" /></div>
                </div>
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Saved to your phone</h2>
                <p className="text-[13px] text-slate-500 mt-1">PDF downloaded · available offline · backed up on DocSite.</p>
                <p className="text-[11px] text-slate-400 mt-2" data-testid="dhf-close-countdown">Closing in {Math.max(1, Math.ceil((1 - ring) * 3))}s…</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
